import Link from 'next/link'
import styles from './styles.module.scss'
import { SUPPORT_EMAIL } from '@/config'
import Seo from '@/components/Seo'

export default function TermsPage() {
  return (
    <div className={styles.container}>
      <Seo title="DROPPP.IO TERMS OF SERVICE" />
      <h1>DROPPP.IO TERMS OF SERVICE</h1>
      <p>
        <b>Last Updated: December 12, 2024</b>
      </p>
      <p>
        Tokenwave LLC (“Tokenwave” or “we”) makes available the Droppp.io
        platform (the “Platform”) through which you can (a) purchase or
        otherwise obtain a unique, blockchain-based non-fungible token created
        by or on behalf of Tokenwave or a third party (each, an “NFT”), (b)
        access one or more works of authorship associated with the NFT and owned
        or controlled by third-party licensors (“Digital Content”) (the NFT and
        Digital Content, collectively, the “Digital Collectible”), (c) hold,
        manage, receive, exchange, or make payments using fiat currency or
        supported digital currency (including, without limitation,
        cryptocurrencies, virtual currencies, or other supported digital
        currency) (“Digital Currency”), and (d) access a marketplace hosted by
        Tokenwave that enables you to resell your Digital Collectibles and
        purchase Digital Collectibles from other collectors (“Droppp
        Marketplace”). These Terms of Service (“Terms”) apply to your access to
        and use of the Platform and services made available on or through the
        Platform (collectively, our “Services”). By using our Services, you
        agree to these Terms, including the mandatory arbitration provision and
        class action waiver in Section 15. If you do not agree to these Terms,
        do not use our Services.
        <br />
        <br />
        We may make changes to these Terms from time to time. If we make
        changes, we will provide you with notice of such changes, such as by
        sending an email, providing a notice through the Platform or updating
        the date at the top of these Terms. Unless we say otherwise in our
        notice, the amended Terms will be effective immediately, and your
        continued use of our Services after we provide such notice will confirm
        your acceptance of the changes. If you do not agree to the amended
        Terms, you must stop using our Services.
        <br />
        <br />
        If you have any questions about these Terms or our Services, please
        contact us at support@droppp.io.
      </p>
      <h3>1. Acquisition of Digital Collectibles</h3>
      <p>
        By acquiring a Digital Collectible via our Services, you expressly
        acknowledge and agree as follows:
      </p>
      <ul>
        <li>
          <u>
            <b>Purchase of NFTs</b>
          </u>
          : By purchasing an NFT, you agree to pay all applicable fees and you
          authorize us to automatically deduct fees directly from your payment.
          You will always be provided with a breakdown of fees prior to your
          purchase of an NFT. All payments made under these Terms will be (a) in
          U.S. Dollars or Digital Currency, as applicable, and (b) by the credit
          or debit card provided to Tokenwave or via blockchain network
          transfers that you initiate using your Droppp Wallet (as defined
          below).
        </li>
        <li>
          <u>
            <b>Digital Content</b>
          </u>
          : Each NFT is a unit of data that is specifically associated with the
          Digital Content. The Digital Content may include copyrighted
          materials, trade names, trademarks or service marks, and names,
          likenesses, or images of third-party licensors or individuals or
          entities associated with those licensors. It is important to
          understand, and you hereby acknowledge and agree, that the NFT is
          separate from the Digital Content. Although the NFT itself is sold or
          otherwise transferred to you, the Digital Content associated with the
          NFT is licensed to you, and not sold or otherwise transferred to you,
          pursuant to the terms and conditions of an end-user license agreement
          between you and the applicable licensor (the “EULA”). By acquiring a
          Digital Collectible via our Services, you expressly agree to enter
          into, and be bound by, the EULA. You represent, warrant and covenant
          that you are, as of your acquisition of the Digital Collectible, and
          will be for the duration of your access to the Digital Collectible, in
          compliance with the EULA.
        </li>
        <li>
          <u>
            <b>Additional Products</b>
          </u>
          : In connection with your acquisition of a Digital Collectible, you
          may be offered the opportunity to acquire one or more products from
          one or more of our affiliated entities (those entities, “Affiliates”
          and those products, “Additional Products”). Unless otherwise expressly
          set forth in writing by one of our Affiliates, all sales of Additional
          Products are final and non-refundable and Additional Products may not
          be returned. However, if you receive a damaged Additional Product,
          unless one of our Affiliates expressly sets forth otherwise in
          writing, your exclusive remedy is limited to one of (a) replacement of
          the damaged Additional Product, subject to availability of replacement
          Additional Products (which is not guaranteed) or (b) a corresponding
          credit for use on the Platform (a “Credit”). The remedy, including
          whether to send a replacement Additional Product or issue you a
          Credit, and the amount of any Credit if issued, is made in our
          Affiliates’ sole and absolute discretion. You may be subject to
          additional terms and conditions with respect to your acquisition of
          Additional Products, in which case you will comply with those terms
          and conditions.
        </li>
      </ul>
      <h3>2. Privacy</h3>
      <p>
        For information about how we collect, use, share or otherwise process
        information about you, please see our{' '}
        <Link href="/privacy">Privacy Notice</Link>.
      </p>
      <h3>3. Eligibility</h3>
      <p>
        In order to use our Services, you must meet, and represent and warrant
        that you do meet, the following eligibility criteria:
      </p>
      <ul>
        <li>You must be at least 18 years of age; and</li>
        <li>You must not be a Restricted Party.</li>
      </ul>
      <p>For purposes of these terms, Restricted Party means:</p>
      <ul>
        <li>
          A person or entity that is the direct or indirect target of any
          applicable financial, economic or trade sanctions or export
          restrictions (collectively, “Sanctions”), including those administered
          or enforced by the U.S. Department of the Treasury’s Office of Foreign
          Assets Control (“OFAC”) and the U.S. Department of Commerce’s Bureau
          of Industry and Security, regardless of whether the person or entity
          is identified on an applicable government list of parties or property
          subject to Sanctions (e.g., OFAC’s list of Specially Designated
          Nationals and Blocked Persons);{' '}
        </li>
        <li>
          A person or entity that is located in, or a citizen or resident of,
          any state, country, territory, or region: (a) that is subject to
          applicable comprehensive geographic Sanctions (e.g., Iran, Cuba, North
          Korea, Syria, the Crimea region of Ukraine, or the so-called Donetsk
          People’s Republic and Luhansk People’s Republic regions of Ukraine),
          (b) designated by the United States government as a “terrorist
          supporting” country, or (c) where the person’s or entity’s entity use
          of the Services would be illegal or otherwise violate any applicable
          law; or
        </li>
        <li>A person or entity otherwise subject to applicable Sanctions.</li>
      </ul>
      <p>
        If you use our Services on behalf of another person or entity, (a) all
        references to “you” throughout these Terms will include that person or
        entity, (b) you represent that you are authorized to accept these Terms
        on that person’s or entity’s behalf, and (c) in the event you or the
        person or entity violates these Terms, the person or entity agrees to be
        responsible to us.
      </p>
      <h3>4. Your User Account and Account Security</h3>
      <p>
        You need to register for an account to access our Services. When you
        register for an account, you must provide accurate account information
        and promptly update this information if it changes. You also must
        maintain the security of your account and promptly notify us if you
        discover or suspect that someone has accessed your account without your
        permission. You must not permit others to use your account credentials.
        You are responsible for the activities of any users that occur in
        connection with your account. We reserve the right to reclaim usernames,
        including on behalf of businesses or individuals that hold legal claim,
        including trademark rights, in those usernames.
      </p>
      <p>Your account is subject to the following conditions:</p>
      <ul>
        <li>
          <b>
            <u>Access:</u>
          </b>{' '}
          You understand and agree that access to your account is limited solely
          to you. You agree that you will not sell, rent, lease, or grant access
          to your account to any person without our prior written permission.
        </li>
        <li>
          <b>
            <u>Security:</u>
          </b>{' '}
          You understand and agree that you are solely responsible for
          maintaining the security of your account and control over any
          usernames, passwords, or any other codes that you use to access our
          Services. Any unauthorized access to your account by third parties
          could result in the loss or theft of Digital Collectibles held in your
          account and any associated accounts, including your credit or debit
          card(s). You understand and agree that you will not hold us
          responsible for managing and maintaining the security of your account.
          You further understand and agree that we are not responsible (and you
          will not hold us responsible) for any unauthorized access to or use of
          your account. You are responsible for monitoring your account. If you
          notice any unauthorized or suspicious activity in your account, please
          notify us immediately.
        </li>
        <li>
          <b>
            <u>Electronic Communication:</u>
          </b>{' '}
          You agree and understand that we will communicate with you via
          electronic means. To ensure that you receive all of our
          communications, you agree to keep your email address and telephone
          number current and notify us if there are any changes. You agree that
          any notices, agreements, disclosures, or other communications
          delivered to your email address on record is considered valid.
        </li>
        <li>
          <b>
            <u>Suspension of Account:</u>
          </b>{' '}
          You agree that we have the right to immediately suspend your account,
          pause or cancel your access to our Services, or close your account if
          we suspect, in our sole discretion, that (a) your account is being
          used for money laundering or any illegal activity, (b) you have
          concealed or provided false identification information or other
          details, (c) you have engaged in fraudulent activity, or (d) you have
          engaged in transactions in violation of these Terms.
        </li>
      </ul>
      <h3>5. Hosted NFT Wallets</h3>
      <ul>
        <li>
          <b>
            <u>Hosted NFT Wallets:</u>
          </b>{' '}
          We will retain the private keys for any NFTs stored in your wallet
          that are hosted on the WAX Blockchain (as defined below) (your “Hosted
          NFT Wallet”), for which private keys are necessary in order to
          transfer any NFTs out of your Hosted NFT Wallet. Tokenwave will use
          the private keys in order to effect the transfers initiated using your
          account.
        </li>
        <li>
          <b>
            <u>Approved External Accounts:</u>
          </b>{' '}
          You may wish to transfer NFTs to and from an account, wallet, or
          address not hosted or controlled by Tokenwave (“External Account”). We
          may require that you verify your control over an External Account or
          satisfy other verification or screening requirements prior to enabling
          transfers between the applicable External Account and your Hosted NFT
          Wallet (any of these External Accounts, an “Approved External
          Account”).
        </li>
        <li>
          <b>
            <u>Transfers to Your Hosted NFT Wallet:</u>
          </b>{' '}
          You may periodically at your discretion transfer from an Approved
          External Account to your Hosted NFT Wallet any NFTs that are supported
          for transfer and storage using our Services. If you transfer to your
          Hosted NFT Wallet any NFTs that are not supported by our Services, or
          if you transfer NFTs specifying an address that does not support the
          NFTs, those NFTs may be permanently lost.
        </li>
        <li>
          <b>
            <u>Transfers from Your Hosted NFT Wallet:</u>
          </b>{' '}
          Subject to any limits on the amounts that you are able to withdraw on
          a daily or other periodic basis, you may periodically at your
          discretion withdraw NFTs by transferring NFTs from your Hosted NFT
          Wallet to an External Account controlled by you. We may require you to
          verify that any External Account to which you seek to transfer NFTs is
          an Approved External Account. You hereby authorize Tokenwave to use
          your Hosted NFT Wallet to send to any External Account specified by
          you the number of NFTs specified by you using our Services.
        </li>
        <li>
          <b>
            <u>Other Terms Applicable to Transfers:</u>
          </b>{' '}
          You will be responsible for: (a) paying all fees charged by any third
          party associated with any External Account or Approved External
          Account and for paying any fees charged by Tokenwave for any
          transfers; (b) ensuring that any transfers are handled in compliance
          with Tokenwave requirements, third-party requirements, NFT
          requirements, and applicable laws, rules, and regulations; (c)
          ensuring that the address to which any NFTs are to be transferred is
          properly formatted and suitable for the type of NFT being transferred;
          and (d) ensuring that there are no errors in any of the transfer
          instructions you provide using our Services. In the event you fail to
          comply with any requirements of this section, the transferred NFTs may
          be permanently lost. Because any blockchain used to confirm transfer
          of any NFT is a decentralized network of independent third parties,
          Tokenwave has no control over them, and therefore cannot and does not
          ensure that any transfer will be confirmed on any blockchain. The
          transfer requests you submit via our Services may not be completed, or
          may be substantially delayed, by the blockchain for the applicable
          NFT. When you complete a transfer request via our Services, you
          authorize Tokenwave to submit your transfer request to the applicable
          blockchain in accordance with the instructions you provide via our
          Services. The timing for completing any transfer will depend on the
          blockchain and other third-party actions that are outside the control
          of Tokenwave or any of its affiliated entities, and Tokenwave makes no
          guarantee regarding whether a transfer will be completed or the amount
          of time it may take to complete any transfer.
        </li>
        <li>
          <b>
            <u>Custodianship:</u>
          </b>{' '}
          You own all NFTs held in your Hosted NFT Wallet or transferred to your
          account. Tokenwave does not own any NFTs that it holds for its
          customers. Although Tokenwave controls the private keys for the
          blockchain addresses for deposited NFTs, you retain control of your
          NFTs while in the custody of Tokenwave, subject to these Terms and
          applicable law. Your NFTs are tracked and maintained on internal
          ledgers maintained by Tokenwave. Tokenwave has no obligation to
          segregate any of your NFTs at a separate blockchain address. Your NFTs
          may be commingled at one or more blockchain addresses with NFTs owned
          by other users or Tokenwave. For security and operational reasons,
          transfers of NFTs by you may be reflected on our internal ledgers
          only, without any transfer on the corresponding blockchain. Private
          keys may be stored by us offline or in electronic or physical vaults
          or other secure locations that may only be accessed in accordance with
          established security procedures, so any transfer of NFTs that will
          involve a transfer on the blockchain may be delayed.
        </li>
        <li>
          <b>
            <u>Conditions and Restrictions on Transfer:</u>
          </b>{' '}
          Tokenwave may at any time and in its sole discretion refuse any
          transfer request or other transaction request submitted via our
          Services, impose limits on the amounts of transfers that can be
          completed on a daily or other periodic basis, or impose any other
          conditions or restrictions upon your use of our Services, without
          prior notice. For example, Tokenwave may: (a) limit the number of NFTs
          that can be purchased or sold on a daily or other periodic basis; (b)
          restrict transaction requests from certain locations; or (c) restrict
          withdrawals or other transactions if there is a reasonable suspicion
          of fraud, diminished capacity, inappropriate activity, or if Tokenwave
          receives reasonable notice that your ownership of some or all of the
          NFTs in your account is in dispute.
        </li>
        <li>
          <b>
            <u>Unclaimed Property:</u>
          </b>{' '}
          If we hold NFTs in your account or Hosted NFT Wallet and have no
          record of your use of our Services for several years, we may be
          required, upon passage of applicable time periods, to report these
          NFTs as unclaimed property in accordance with the abandoned property
          and escheat laws. If this occurs, we will use reasonable efforts to
          give you written notice. If you fail to respond within seven business
          days or as required by law, we may be required to deliver any such
          NFTs to the applicable state or jurisdiction as unclaimed property.
          The applicable governmental entity may require Tokenwave to liquidate
          any NFTs in your Hosted NFT Wallet into fiat currency and turn over
          the resulting fiat currency to the governmental entity. You agree that
          Tokenwave will not have any responsibility or liability for any
          losses, unrealized gains, damages, expenses, or other harm that you
          may incur in connection with Tokenwave turning over digital assets or
          fiat currency to the applicable governmental entity or Tokenwave’s
          liquidation of your NFTs. We reserve the right to deduct a dormancy
          fee or administrative fee from such unclaimed funds, as permitted by
          applicable law.
        </li>
        <li>
          <b>
            <u>Property Disputes:</u>
          </b>{' '}
          If we receive notice that any NFTs held in your Hosted NFT Wallet are
          alleged to have been stolen or otherwise not lawfully possessed by
          you, we may, but have no obligation to, place an administrative hold
          on the affected NFTs or your Hosted NFT Wallet. If we place an
          administrative hold on some or all of your NFTs, we may continue the
          hold until the time when the dispute has been resolved and evidence of
          the resolution acceptable to Tokenwave has been provided to Tokenwave
          in a form acceptable to Tokenwave. Tokenwave will not involve itself
          in any such dispute or the resolution of the dispute. We will have no
          liability or responsibility for any such hold, or for your inability
          to transfer NFTs during the period of any hold.
        </li>
      </ul>
      <h3>6. Droppp Wallet; Payment Services</h3>

      <ul>
        <li>
          <b>
            <u>Droppp Wallet:</u>
          </b>{' '}
          <ol>
            <li>
              In connection with our Services, we may permit you to hold,
              manage, receive, exchange and make payments using Digital
              Currency, which occur through a non-custodial wallet (a “Droppp
              Wallet”) made available to you through the Services. We utilize a
              Third-Party Service for the Droppp Wallet and key management
              infrastructure from Turnkey Global Inc. (“Turnkey”). If you create
              a Droppp Wallet, you acknowledge that Turnkey is our Third-Party
              Vendor and you agree to and accept the Turnkey Terms of Service
              (available at https://www.turnkey.com/legal/terms or any successor
              website), as may be updated by Turnkey from time to time. The
              Droppp Wallet will enable you to: (i) hold and manage the Digital
              Currency on your Droppp Wallet (“Droppp Balance”), (ii) receive
              deposits of USDC on the Solana blockchain (“USDC on Solana”); (iv)
              make withdrawals of USDC on Solana from your Droppp Balance; and
              (v) otherwise make transfers of USDC on Solana to other Droppp
              Marketplace users and to Tokenwave in connection with purchases
              and sales of Digital Collectibles on the Droppp Marketplace. You
              acknowledge and agree that we and Turnkey are not liable for any
              unsupported digital assets that are sent to your Droppp Wallet
            </li>
            <li>
              The Droppp Wallet is non-custodial. Neither we nor Turnkey have
              unencrypted access to (a) your Droppp Wallet’s private keys, which
              are used to sign blockchain transactions and thereby control the
              funds you hold in your Droppp Wallet, or (b) the seed phrase(s)
              used to generate your Droppp Wallet’s public/private key pair(s).
              Tokenwave and Turnkey provide certain services that support your
              ability to access, and initiate on-chain transfers of, funds held
              in your Droppp Wallet, but Tokenwave and Turnkey do not have the
              ability to move or transfer your funds in your Droppp Wallet and
              are not otherwise acting as a custodian of such funds.
            </li>
            <li>
              In order to engage with the Droppp Marketplace, you must create
              your Droppp Wallet. To create the Droppp Wallet you will be
              prompted to (i) provide a recovery email address (“Recovery
              Email”) that will be used by Turnkey to communicate with you if
              you initiate an email recovery flow in order to regain access to
              your Droppp Wallet and (ii) set up a “Passkey” which will serve as
              your authentication method when managing funds in your Droppp
              Wallet. The Passkey is generated and stored by your local device
              (or created using password synchronization tools like Google
              Password Manager or iCloud Keychain). Your Passkey enables you to
              authenticate and create sessions within the Droppp Wallet and sign
              blockchain transactions using your Droppp Wallet’s private key.
            </li>
            <li>
              You are solely responsible for the safekeeping and security of
              your Recovery Email, Passkey, any private keys, seed phrase, and
              any other account credentials (“Account Credentials”). Any loss of
              or failure to properly maintain the security and availability of
              Account Credentials may result in a permanent inability to access
              your Droppp Wallet or any Droppp Balance or other asset held in
              your Droppp Wallet. You will be responsible for keeping all
              Account Credentials secure and for any and all activity associated
              with your Droppp Wallet and your account.
            </li>
            <li>
              If you lose your Account Credentials or are otherwise unable to
              reconnect to your Droppp Wallet, you will be unable to access your
              Droppp Balance or any assets or Digital Currency associated with
              the Droppp Wallet, withdraw from your Droppp Balance, or make any
              purchases on the Droppp Marketplace. You may be able to restore
              access to your Droppp Wallet by (i) initiating an email recovery
              flow, which requires that you are able to receive and access
              emails sent to your Recovery Email or (ii) importing your private
              keys and/or seed phrase(s) within a third-party wallet application
              if you have used the private key export functionality provided by
              Turnkey and made accessible to you through the Services (“Private
              Key Export”), which allows you to access in unencrypted form the
              private key(s) for your Droppp Wallet and/or the seed phrase(s)
              used to generate your Droppp Wallet. WARNING: Private Key Export
              exposes you and your Digital Currency to security risks. Anyone
              who is able to access your private keys or seed phrase in an
              unencrypted form will be able to access, transfer, manage, or
              spend your Digital Currency. NEITHER TOKENWAVE NOR TURNKEY
              (defined below) SHALL BE RESPONSIBLE FOR OR LIABLE IN CONNECTION
              WITH ANY EXPORTED PRIVATE KEYS OR SEED PHRASE, AND YOU SHALL HOLD
              TOKENWAVE AND TURNKEY HARMLESS FROM ANY LIABILITY ARISING IN
              CONNECTION THERETO.
            </li>
            <li>
              Should we or Turnkey suddenly go offline, cease offering all or
              parts of the Services, or providing the Third-Party Services or
              infrastructure for any reason, you could potentially lose control
              and access to the Droppp Wallet forever, including all Digital
              Currency therein. You are responsible for Private Key Export in
              order to move the Digital Currency to another wallet address to
              account for this risk.
            </li>
            <li>
              You acknowledge that funds, including the Droppp Balance, held by
              you in your Droppp Wallet are not subject to protections or
              insurance provided by the Federal Deposit Insurance Corporation or
              the Securities Investor Protection Corporation.
            </li>
            <li>
              You agree and acknowledge that (i) Tokenwave and Turnkey will have
              no obligation or responsibility with respect to any Account
              Credentials, (ii) Tokenwave and Turnkey are not liable or
              responsible for the risks associated with use of a non-custodial
              wallet, including those set forth in this Section 6, and (iii)
              after termination of your account, Tokenwave and Turnkey will have
              no obligation to support Transaction Instructions (defined below)
              related to your Droppp Wallet.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <u>Deprecation of Custodial Wallet:</u>
          </b>{' '}
          <ol>
            <li>
              If you have not created a Droppp Wallet as set forth in the
              foregoing Section, but currently have a Droppp Balance in a
              pre-existing custodial digital asset wallet provided by Circle
              Internet Financial, LLC and its affiliated entities (collectively,
              “Circle”) (“Prior Wallet”), you may migrate that Droppp Balance
              either by (i) creating a new Droppp Wallet as set forth above and
              transferring the Droppp Balance held by Circle in the Prior Wallet
              to the Droppp Wallet, or (ii) transferring the Prior Wallet’s
              Droppp Balance to an external address. If you choose to transfer
              your Prior Wallet’s Droppp Balance to your Droppp Wallet, such
              funds will continue to be in the form of USDC but may be
              transferred or converted from one blockchain to another (e.g.,
              USDC on the Ethereum blockchain to USDC on Solana). If you do not
              timely transfer such funds from the Prior Wallet, you acknowledge
              that there is a risk that you will be unable to access such funds
              held by Circle in the Prior Wallet and you could potentially lose
              control and access to the Prior Wallet and such funds forever,
              including all Digital Currency therein.
            </li>
            <li>
              The Prior Wallet will be configured to only be able to withdraw
              funds. If funds are sent to the Prior Wallet, they may be
              misdelivered and lost forever. Without limiting any other
              provisions of these Terms, you acknowledge and agree that we have
              no liability or responsibility with respect to such misdelivered
              funds and have no obligation to attempt to recover such
              misdelivered funds.
            </li>
            <li>
              If you have any pending listings on the Droppp Marketplace that
              were posted while using the Prior Wallet, those listings will be
              or have been cancelled. In order to post listings or make
              purchases on Droppp Marketplace, you are required to have a Droppp
              Wallet. Once you have created a Droppp Wallet, such listings may
              be created.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <u>Payment Services:</u>
          </b>{' '}
          <ol>
            <li>
              We may make available the ability to purchase or otherwise obtain
              Tokenwave goods or services or third party goods or services,
              including Digital Collectibles, through the Services or otherwise
              receive and send Digital Currencies with your Droppp Wallet (each,
              a “Transaction”). You may be able to make Transactions on the
              Services using fiat currency or Digital Currency (each, a “Payment
              Service”). If you wish to make a Transaction, you may be asked to
              supply certain relevant information, such as your payment card
              number and its expiration date, your billing address, and your
              shipping information, to us or our payment processors (“Payment
              Processors”). You represent and warrant that you have the right to
              use any payment card that you submit in connection with a
              Transaction. We may receive updated information from your issuing
              bank or Payment Processors about any payment method you have
              stored with us. You authorize us to charge your payment method,
              including any updated payment method information we receive, for
              any charges you are responsible for under these Terms.
              Verification or further provision of information may be required
              prior to the acknowledgment or completion of any Transaction. You
              will pay all charges incurred by you or on your behalf through the
              Services, at the prices in effect when such charges are incurred,
              including all taxes. ’
            </li>
            <li>
              In using any Payment Services, you may submit to us Transaction
              instructions (“Transaction Instructions”), which we may need to
              pass on to our Payment Processors or other Third-Party Vendors
              (e.g., Turnkey to process your request to sign a blockchain
              transaction or to third-party node operators that broadcast your
              Transaction to the blockchain). You assume full responsibility
              for, and we will not be responsible or liable for any loss or
              damage arising out of, any and all Transaction Instructions we
              receive from you (including, without limitation, the authenticity
              of those Transaction Instructions) and we are entitled to assume
              that those Transaction Instructions were authentically and
              correctly submitted by you (whether or not those Transaction
              Instructions were, in fact, submitted by you or were erroneously
              submitted by you). We are under no obligation to verify that those
              Transaction Instructions are, in fact, submitted by you or are
              otherwise non-erroneous. You must verify all Transaction
              information prior to submitting Transaction Instructions, as
              Transactions may not be subject to cancelation or reversal. We
              reserve the right, in our sole discretion, to refuse to submit any
              Transaction Instructions, limit the submission of any Transaction
              Instructions, or cancel any pending Transaction Instructions,
              including, without limitation, for purposes of complying with
              applicable laws, rules, or regulations.
            </li>
            <li>
              Any Transaction Instructions will only be deemed “received” when
              you receive notification that they have been successfully
              processed. Transaction Instructions and information transmitted
              via any Payment Services are generally transmitted via the
              Internet and may be routed via public, non-protected
              communications technology. We cannot guarantee that your
              Transaction Instructions or information so transmitted will in
              fact be completely protected against unauthorized access and you
              accept this risk. We are not liable for any delays or inabilities
              in processing Transaction Instructions.
            </li>
            <li>
              We are not responsible or liable for processing Transactions,
              including, without limitation, any partially completed
              Transactions, delayed Transactions, mistakenly processed
              Transactions, or unprocessed Transactions, and we make no
              representations, warranties, or guarantees with respect to any
              Transaction, including, without limitation, that any Transaction
              will be timely processed or processed at all or that any amounts
              will be refunded.{' '}
            </li>
          </ol>
        </li>
        <li>
          <b>
            <u>Registration Process and Identity Verification:</u>
          </b>{' '}
          <ol>
            <li>
              In connection with creating an account pursuant to Section 4,
              creating your Droppp Wallet, or otherwise using the Services to
              make a Transaction, you must provide current, complete, and
              accurate information for all required elements on the registration
              page and as otherwise requested. You represent, warrant and
              covenant that all information you provide to us, our Verification
              Services Provider (as defined below) and Payment Processors,
              including without limitation, Additional Information (as defined
              below) is current, complete and accurate in all respects.
            </li>
            <li>
              Depending on the specific Payment Services that you use or the
              Transactions in which you engage when using the Payment Services,
              we may require you to submit additional information,
              documentation, or records, which may include, without limitation,
              name, date of birth, residential address, unique identifying
              numbers (including, without limitation, Social Security Numbers
              for U.S. users), government-issued identification, or information
              required for Sanctions screening or other enhanced “due diligence”
              or “know-your-client” screening procedures (“Additional
              Information”) to us, our third-party designee who provides
              identity verification or other information verification services
              to us (currently, Persona Identities, Inc. or its affiliated
              entities) (our “Verification Services Provider”), or Payment
              Processors directly. You will provide to us or our Verification
              Services Provider (or Payment Processors), when registering for an
              account or creating a Droppp Wallet, and on an ongoing basis, any
              information, documentation and records (including, without
              limitation, Additional Information) that we or our Payment
              Processors deem necessary to provide any Payment Services.
            </li>
            <li>
              We may, in our sole discretion and without providing reasons,
              refuse your application for, or to suspend, terminate or limit
              your use of, any Payment Services, including the Droppp Wallet, or
              to change the eligibility criteria for registration or use of any
              Payment Services at any time. We may confidentially verify,
              supplement, or append the information you provide us with or
              obtain information on you ourselves or through third parties from
              secure databases. We or a third party on our behalf (including,
              without limitation, the Verification Services Provider) may
              verify, supplement, or amend your information for any reason
              including for the up-to-date maintenance of our records or to
              comply with applicable reporting obligations.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <u>Disclaimers and Acknowledgements of Risk:</u>
          </b>{' '}
          TRANSACTING IN DIGITAL CURRENCY INVOLVES SUBSTANTIAL RISK. BY USING
          THE PAYMENT SERVICES, YOU EXPRESSLY ACKNOWLEDGE AND AGREE TO THE RISKS
          AND RELEASES SET FORTH IN SECTION 13 BELOW.
        </li>
        <li>
          <b>
            <u>Additional Representations and Warranties:</u>
          </b>{' '}
          Without limiting any other representations, warranties, or covenants
          in these Terms, you represent, warrant and covenant that:
          <ol>
            <li>You are not, nor will you become, a Restricted Party; </li>
            <li>
              You are the sole ultimate beneficial owner of your Droppp Wallet,
              are not acting on behalf of or representing any other natural
              person, legal person or legal entity, and you have the sole right
              and authority to submit Transactions on your behalf;
            </li>
            <li>
              You are the beneficial owner of (or if you are acting as a
              trustee, the legal owner) any Digital Currency subject to these
              Terms; and
            </li>
            <li>
              You are not relying on any communication (written or oral) of
              Tokenwave as advice or recommendation to engage in any
              Transaction, including, without limitation, any Transaction
              involving Digital Currency and that Tokenwave has not (a) given
              any guarantee or representation as to the potential success,
              return, effect, or benefit (either legal, regulatory, tax,
              financial, accounting, or otherwise) of transacting in Digital
              Currency or (b) made any representation to you regarding the
              legality of transacting in Digital Currency under applicable laws,
              rules, or regulations.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <u>Termination or Suspension:</u>
          </b>{' '}
          We may at any time, and without liability to you, (a) delay
          submitting, or refuse to submit, any Transaction or (b) terminate,
          suspend, or limit your use of the Payment Services (including freezing
          any Digital Currency in your account, freezing or closing your Droppp
          Wallet for any reason, including, without limitation: (i) in the event
          of any breach by you of these Terms or our Payment Processor Terms;
          (ii) for the purposes of complying with applicable laws, rules, or
          regulations; (iii) where we suspect that a Transaction you attempt to
          initiate is potentially connected to any unlawful or suspicious
          activities (including, without limitation, money laundering, terrorism
          financing, or fraudulent activities); or (iv) where your Droppp Wallet
          does not have the requisite funds required to complete a Transaction
          that you initiate or attempt to initiate.
        </li>
      </ul>

      <h3>7. Proprietary Rights</h3>
      <ul>
        <li>
          <b>
            <u>Ownership of Services:</u>
          </b>{' '}
          Any and all software, documentation, graphics, or other works of
          authorship, as well as any and all inventions, trademarks, data,
          information, or other materials used, displayed, or made available in
          connection with our Services, except for Third-Party Services (as
          defined below) (“Tokenwave Materials”) together with all intellectual
          property rights in the Tokenwave Materials, as between you and
          Tokenwave, owned by Tokenwave.
        </li>
        <li>
          <b>
            <u>License:</u>
          </b>{' '}
          Subject to your compliance with these Terms, you are hereby granted a
          limited, nonexclusive, nontransferable, non-sublicensable, revocable
          license to access and use the Tokenwave Materials for your own
          personal, noncommercial use in connection with your use of our
          Services.
        </li>
        <li>
          <b>
            <u>Limitations:</u>
          </b>{' '}
          You will not, and will not permit any third party to, (a) resell,
          lease, lend, share, distribute or otherwise permit any third party to
          use any Tokenwave Materials or use any Tokenwave Materials in any
          service bureau environment, (b) modify or create derivative works of
          any Tokenwave Materials, or any portion thereof, (c) frame, display,
          or incorporate any Tokenwave Materials in any website or any other
          work of authorship, (d) decompile, disassemble, reverse engineer, or
          attempt to discover the source code of any Tokenwave Materials, (e)
          use any Tokenwave Materials to design, develop, or create any
          competing product or service, or (f) otherwise use any Tokenwave
          Materials for any commercial or noncommercial purpose other than their
          intended purposes determined at Tokenwave’s discretion. “Tokenwave,”
          any product or service names, logos, and other marks used on or
          connection with our Services or Tokenwave Materials, are trademarks
          owned by Tokenwave or its licensors. You may not copy, imitate, or use
          them without Tokenwave’s prior written consent.
        </li>
        <li>
          <b>
            <u>User Content:</u>
          </b>{' '}
          You hereby grant to Tokenwave a royalty-free, fully paid-up,
          sublicensable, transferable, perpetual, irrevocable, non-exclusive,
          worldwide license to use, copy, modify, create derivative works of,
          display, perform, publish, and distribute, in any form, medium or
          manner, any text, information, data, documents, images, or other
          content you provide to Tokenwave using our Services that is not
          Feedback owned by Tokenwave (the “User Content”). You represent and
          warrant that: (a) you own the User Content or have the right to grant
          the rights and licenses in these Terms; and (b) the User Content and
          use by Tokenwave of the User Content as licensed herein does not and
          will not violate, misappropriate, or infringe on the rights of any
          third party. Tokenwave may remove any User Content from our Services
          for any reason at Tokenwave’s discretion.
        </li>
      </ul>

      <h3>8. Prohibited Conduct and Content</h3>
      <p>
        You will not violate any applicable law, contract, intellectual property
        right or other third-party right or commit a tort, and you are solely
        responsible for your conduct while using our Services. You will not:
      </p>
      <ul>
        <li>
          Use the Services, including the Payment Services and your Hosted NFT
          Wallet, to cause an inbound or outbound transfer of fiat currency,
          NFTs, or Digital Currency where you have reason to know that any
          person or entity involved in the transfer—whether directly or
          indirectly—is a Restricted Party (as defined above), including but not
          limited to the following persons or entities (a) the originator of the
          transfer, (b) the recipient of the transfer, or (c) the digital asset
          exchange, wallet service provider, bank, or other financial
          institution involved in the transfer;
        </li>
        <li>
          Engage in any harassing, threatening, intimidating, predatory or
          stalking conduct;
        </li>
        <li>
          Use or attempt to use another user’s account without authorization
          from that user and Tokenwave;
        </li>
        <li>
          Impersonate or post on behalf of any person or entity or otherwise
          misrepresent your affiliation with a person or entity;
        </li>
        <li>Sell, resell or commercially use our Services;</li>
        <li>
          Copy, reproduce, distribute, publicly perform or publicly display all
          or portions of our Services, except as expressly permitted by us or
          our licensors;
        </li>
        <li>
          Modify our Services, remove any proprietary rights notices or
          markings, or otherwise make any derivative works based upon our
          Services;
        </li>
        <li>
          Use our Services other than for their intended purpose and in any
          manner that could interfere with, disrupt, negatively affect or
          inhibit other users from fully enjoying our Services or that could
          damage, disable, overburden or impair the functioning of our Services
          in any manner;
        </li>
        <li>
          Reverse engineer any aspect of our Services or do anything that might
          discover source code or bypass or circumvent measures employed to
          prevent or limit access to any part of our Services;
        </li>
        <li>
          Attempt to circumvent any content-filtering techniques we employ or
          attempt to access any feature or area of our Services that you are not
          authorized to access;
        </li>
        <li>
          Use any data mining, robots or similar data gathering or extraction
          methods designed to scrape or extract data from our Services;
        </li>
        <li>
          Develop or use any applications that interact with our Services
          without our prior written consent;
        </li>
        <li>
          Send, distribute or post spam, unsolicited or bulk commercial
          electronic communications, chain letters, or pyramid schemes;
        </li>
        <li>Bypass or ignore instructions contained in our robots.txt file;</li>
        <li>
          Market our Services, any Digital Collectibles, any NFTs, or any
          Digital Currency explicitly or implicitly as an investment or an
          opportunity to obtain an economic benefit or profit, engage in any
          price manipulation activity with respect to our Services, any Digital
          Collectibles, any NFTs, or any Digital Currency, or use our Services,
          any Digital Collectibles, any NFTs, or any Digital Currency to engage
          in any investment-related activities, including, without limitation,
          any speculative activity with respect to our Services, any Digital
          Collectibles, any NFTs, or any Digital Currency; or
        </li>
        <li>
          Use our Services for any unauthorized purpose or in violation of
          applicable laws, rules, or regulations, or engage in, encourage or
          promote any activity that violates these Terms.
        </li>
      </ul>
      <p>
        You may also post or otherwise share only User Content that is
        nonconfidential and that you have all necessary rights to disclose. You
        may not create, post, store or share any User Content that:
      </p>
      <ul>
        <li>
          Is unlawful, libelous, defamatory, obscene, pornographic, indecent,
          lewd, suggestive, harassing, threatening, invasive of privacy or
          publicity rights, abusive, inflammatory, fraudulent, misleading, or
          false;
        </li>
        <li>
          Would constitute, encourage or provide instructions for a criminal
          offense, violate the rights of any party or otherwise create liability
          or violate any local, state, national or international law;
        </li>
        <li>
          May infringe any patent, trademark, trade secret, copyright or other
          intellectual or proprietary right of any party;
        </li>
        <li>
          Impersonates, or misrepresents your affiliation with, any person or
          entity;
        </li>
        <li>
          Contains any unsolicited promotions, political campaigning,
          advertising or solicitations;
        </li>
        <li>
          Contains any private or personal information of a third party without
          such third party’s consent;
        </li>
        <li>
          Contains any viruses, corrupted data or other harmful, disruptive or
          destructive files or content; or
        </li>
        <li>
          In our sole judgment, is objectionable, restricts or inhibits any
          other person from using or enjoying our Services, or may expose
          Tokenwave or others to any harm or liability of any type.
        </li>
      </ul>
      <p>
        Enforcement of this Section 8 is solely at Tokenwave’s discretion, and
        failure to enforce this section in some instances does not constitute a
        waiver of our right to enforce it in other instances. In addition, this
        Section 8 does not create any private right of action on the part of any
        third party or any reasonable expectation that our Services will not
        contain any content that is prohibited by such rules.
      </p>

      <h3>9. Repeat Infringer Policy; Copyright Complaints</h3>
      <p>
        In accordance with the Digital Millennium Copyright Act (“DMCA”) and
        other applicable law, we have adopted a policy of terminating, in
        appropriate circumstances, the accounts of users who repeatedly infringe
        the intellectual property rights of others. If you believe that anything
        on our Services infringes any copyright that you own or control, you may
        send us a written notice that includes all of the following:
      </p>
      <ul>
        <li>
          a legend or subject line that says: “DMCA Copyright Infringement
          Notice”;
        </li>
        <li>
          a legend or subject line that says: “DMCA Copyright Infringement
          Notice”;
        </li>
        <li>
          a description of the copyrighted work that you claim has been
          infringed;
        </li>
        <li>
          the URL of the site and a description of where the material that you
          claim is infringing is located on that site;
        </li>
        <li>your address, telephone number, and e-mail address;</li>
        <li>
          a statement by you that you have a good faith belief that the alleged
          infringing material is not authorized by the copyright owner, its
          agent, or the law;
        </li>
        <li>
          a statement by you, made under penalty of perjury, that all the
          information in your notice is accurate, and that you are the copyright
          owner (or, if you are not the copyright owner, then your statement
          must indicate that you are authorized to act on the behalf of the
          owner of an exclusive right that is allegedly infringed); and
        </li>
        <li>your electronic or physical signature.</li>
      </ul>
      <p>
        Tokenwave will only receive DMCA notices by mail, email, or facsimile
        directed to its designated agent at the addresses below:
      </p>
      <dl>
        <dt>By mail:</dt>
        <dd>Tokenwave LLC</dd>
        <dd>Attention: Legal Department</dd>
        <dd>2802 Wetmore Avenue</dd>
        <dd>Everett, WA 98203</dd>
        <dd>
          By email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </dd>
      </dl>
      <p>
        Please note that if you knowingly misrepresent that any activity or
        material on our Services is infringing, you may be liable to Tokenwave
        for certain costs and damages.
      </p>
      <h3>10. Indemnification</h3>
      <p>
        To the fullest extent permitted by applicable law, you will indemnify,
        defend and hold harmless Tokenwave and our subsidiaries and affiliates,
        our Third Party Vendors, and each of our respective officers, directors,
        agents, partners and employees (individually and collectively, the
        “Tokenwave Parties”) from and against any losses, liabilities, claims,
        demands, damages, expenses or costs (“Claims”) arising out of or related
        to (a) your acquisition of any Digital Collectibles (including, without
        limitation, your purchase or sale of any NFTs; (b) your use of our
        Services (including, without limitation, the Droppp Wallet and Payment
        Services); (c) your violation of these Terms or any terms referenced in
        these Terms; or (d) your violation of any applicable laws, rules, or
        regulations (including, without limitation, (i) the Commodity Exchange
        Act and the regulations promulgated thereunder by the CFTC (as defined
        below), (ii) U.S. federal securities laws and the regulations
        promulgated thereunder by the SEC (as defined below) and (iii) U.S.
        state securities laws and the regulations promulgated thereunder by the
        applicable state regulatory authority) or the rights of any other person
        or entity.
      </p>
      <p>
        You agree to cooperate with the Tokenwave Parties in defending such
        Claims and pay all fees, costs and expenses associated with defending
        such Claims (including, but not limited to, attorneys’ fees). You also
        agree that the Tokenwave Parties will have control of the defense or
        settlement, at Tokenwave’s sole option, of any third-party Claims.
      </p>

      <h3>11. Disclaimers</h3>
      <p>
        <b>
          YOUR USE OF OUR SERVICES IS AT YOUR SOLE RISK. EXCEPT AS OTHERWISE
          PROVIDED IN A WRITING BY US, OUR SERVICES (INCLUDING ANY THIRD-PARTY
          SERVICES), ANY CONTENT IN OUR SERVICES, AND ALL DIGITAL COLLECTIBLES
          ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY
          KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO,
          IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, TITLE, AND NON-INFRINGEMENT. IN ADDITION, TOKENWAVE DOES NOT
          REPRESENT OR WARRANT THAT OUR SERVICES ARE ACCURATE, COMPLETE,
          RELIABLE, CURRENT OR ERROR-FREE. WHILE TOKENWAVE ATTEMPTS TO MAKE YOUR
          USE OF OUR SERVICES SAFE, WE CANNOT AND DO NOT REPRESENT OR WARRANT
          THAT OUR SERVICES OR SERVERS ARE FREE OF VIRUSES OR OTHER HARMFUL
          COMPONENTS. YOU ASSUME THE ENTIRE RISK AS TO THE QUALITY AND
          PERFORMANCE OF OUR SERVICES.
        </b>
      </p>
      <p>
        <b>
          WITHOUT LIMITING THE FOREGOING, YOU ASSUME THE ENTIRE RISK OF ENGAGING
          IN PEER-TO-PEER PAYMENTS OR OTHER TRANSACTIONS WITH OTHER USERS OF OUR
          SERVICES (“PEER-TO-PEER TRANSACTIONS”), INCLUDING, WITHOUT LIMITATION,
          (I) ANY FRAUD, THEFT, MISREPRESENTATION, OR OTHER MISCONDUCT OF OTHER
          USERS, (II) THAT ANY PRODUCTS OR SERVICES ACQUIRED FROM ANY OTHER USER
          (INCLUDING, WITHOUT LIMITATION, ANY DIGITAL COLLECTIBLES OR NFTS) IN
          PEER-TO-PEER TRANSACTIONS MAY NOT BE MERCHANTABLE, FIT FOR ALL OR ANY
          PURPOSES, AUTHENTIC, OR OTHERWISE MEET YOUR EXPECTATIONS OR ANY
          REPRESENTATIONS MADE BY THAT USER WITH RESPECT TO THOSE PRODUCTS OR
          SERVICES (WHETHER VISUAL, WRITTEN, ORAL, OR OTHERWISE), (III) THAT THE
          USER WITH WHOM YOU ENGAGE IN PEER-TO-PEER TRANSACTIONS HOLDS
          MARKETABLE TITLE TO THE PRODUCT OR SERVICE OFFERED OR SOLD TO YOU, OR
          THAT SUCH USER HAS THE RIGHTS NECESSARY TO CONVEY THE RIGHTS IN THE
          PRODUCT OR SERVICE PROVIDED TO YOU, AND (IV) ANY OTHER CONDUCT OF THE
          USER WITH WHOM YOU ENGAGE IN PEER-TO-PEER-TRANSACTIONS.
        </b>
      </p>
      <p>
        <b>
          TOKENWAVE AND THE TOKENWAVE PARTIES WILL NOT BE RESPONSIBLE OR LIABLE
          TO YOU FOR ANY LOSS OF, AND TAKE NO RESPONSIBILITY FOR ANY USE OF,
          DIGITAL COLLECTIBLES, DIGITAL CURRENCY, OR FIAT CURRENCY, INCLUDING,
          WITHOUT LIMITATION, ANY LOSSES, DAMAGES OR CLAIMS ARISING FROM: (A)
          USER ERROR SUCH AS FORGOTTEN PASSWORDS OR ACCOUNT CREDENTIALS,
          INCORRECTLY CONSTRUCTED TRANSACTIONS, MISDELIVERED TRANSACTIONS,
          UNSUPPORTED DIGITAL COLLECTIBLES OR DIGITAL CURRENCIES, OR MISTYPED
          ADDRESSES; (B) SERVER FAILURE OR DATA LOSS; (C) CORRUPTED WALLET
          FILES; (D) UNAUTHORIZED ACCESS TO APPLICATIONS; (E) ANY THIRD PARTY
          ACTIVITIES, INCLUDING, WITHOUT LIMITATION, (I) FRAUD, THEFT,
          MISREPRESENTATION, OR OTHER MISCONDUCT OR (II) THE USE OF VIRUSES,
          PHISHING, BRUTEFORCING OR OTHER MEANS OF ATTACK AGAINST OUR SERVICES,
          DIGITAL COLLECTIBLES, DIGITAL CURRENCY, OR FIAT CURRENCY; OR (F) ANY
          OF THE MATTERS DESCRIBED IN SECTION 13 BELOW.
        </b>
      </p>
      <p>
        <b>
          NFTS AND DIGITAL CURRENCY ARE INTANGIBLE DIGITAL ASSETS. THEY EXIST
          ONLY BY VIRTUE OF THE OWNERSHIP RECORD MAINTAINED ON A BLOCKCHAIN. ANY
          TRANSFER OF TITLE THAT MIGHT OCCUR IN ANY DIGITAL CURRENCY OR NFT
          OCCURS ON THE DECENTRALIZED LEDGER WITHIN THE APPLICABLE BLOCKCHAIN.
          WE DO NOT GUARANTEE THAT THE TRANSFER OF TITLE OR RIGHT IN ANY DIGITAL
          CURRENCY OR NFTS CAN BE EFFECTED.
        </b>
      </p>
      <p>
        <b>
          You are responsible for complying with all applicable laws, rules and
          regulations, including, but not limited to, the Commodity Exchange Act
          and the regulations promulgated thereunder by the U.S. Commodity
          Futures Trading Commission (“CFTC”), the U.S. federal securities laws
          and the regulations promulgated thereunder by the U.S. Securities and
          Exchange Commission (“SEC”), and any U.S. state securities laws and
          the regulations promulgated thereunder by the applicable state
          regulatory authority. You understand that Tokenwave is not registered
          or licensed by the CFTC, the SEC, or any financial regulatory
          authority. No financial regulatory authority has reviewed or approved
          the use of our Services. Our Services do not constitute advice or a
          recommendation concerning any commodity, security or other asset.
          Tokenwave is not acting as an investment adviser or commodity trading
          adviser to any person. Tokenwave is not your broker, lawyer,
          intermediary, agent, or advisor and has no fiduciary relationship or
          obligation to you regarding any other decisions or activities that you
          effect when using our Services. Neither our communications nor any
          information that we provide to you is intended as, or will be
          considered or construed as, advice. Tokenwave makes no recommendation
          and does not provide any advice about the value of any Digital
          Collectible or Digital Currency. EACH DIGITAL COLLECTIBLE IS INTENDED
          FOR CONSUMER ENJOYMENT, USE AND CONSUMPTION ONLY. IT IS NOT A
          “SECURITY,” AS DEFINED UNDER THE U.S. FEDERAL SECURITIES LAWS, OR
          UNDER THE SECURITIES LAWS OF ANY U.S. STATE.
        </b>
      </p>
      <h3>12. Limitation of Liability</h3>
      <p>
        <b>
          To the fullest extent permitted by applicable law, Tokenwave and the
          other Tokenwave Parties will not be liable to you under any theory of
          liability—whether based in contract, tort, negligence, strict
          liability, warranty, or otherwise—for any indirect, consequential,
          exemplary, incidental, punitive or special damages or lost profits,
          even if Tokenwave or the other Tokenwave Parties have been advised of
          the possibility of such damages.
        </b>
      </p>
      <p>
        <b>
          The total liability of Tokenwave and the other Tokenwave Parties for
          any claim, regardless of the form of the action, (a) arising out of or
          relating to these Terms or our Services (but not your purchase or
          acquisition of an Additional Product) is limited to the greater of
          $100 or the amount in USD paid by you to Tokenwave for the products or
          services on our Services that gives rise to the liability and (b)
          arising out of your purchase or acquisition of an Additional Product
          is limited to (i) replacement of the Additional Product or (ii) the
          Credit, each as determined in accordance with Section 1. None of the
          Tokenwave Parties will have any liability for any Peer-to-Peer
          Transactions.
        </b>
      </p>
      <p>
        <b>
          The limitations set forth in this Section 12 will not limit or exclude
          liability for the gross negligence, fraud or intentional misconduct of
          Tokenwave or the other Tokenwave Parties or for any other matters in
          which liability cannot be excluded or limited under applicable law.
          Additionally, some jurisdictions do not allow the exclusion or
          limitation of incidental or consequential damages, so the above
          limitations or exclusions may not apply to you.
        </b>
      </p>

      <h3>13. Acknowledgement of Risks and Release</h3>
      <p>
        There are risks associated with the acquisition, use, holding, exchange
        and transfer of, and otherwise transacting in, Digital Collectibles and
        Digital Currency (including, without limitation, purchase, sale,
        storage, and transfer of NFTs) and use of our Services (including,
        without limitation, the Payment Services) that may cause you to incur
        financial losses including, without limiting, the following:
      </p>
      <ul>
        <li>
          Your Digital Collectibles or Digital Currency may be stolen or
          accessed (or your Digital Collectibles may be copied) without your
          authorization, including, without limitation, due to theft, loss, or
          mishandling of applicable private keys;
        </li>
        <li>
          You may be unable to collect Digital Collectibles or receive Digital
          Currency if you are unwilling or unable to create the applicable
          account or enter the applicable code or other mechanism for
          identifying your purchase transaction;
        </li>
        <li>
          • The existence of any Digital Collectible or Digital Currency may be
          compromised due to technological failure, technological progression,
          changes in operating rules of the underlying protocols of a Digital
          Collectible or Digital Currency (commonly known as “forks”),
          third-party attacks (including, without limitation, attacks on
          third-party systems on which our Services may rely, such as the
          Payment Processor, banks, credit card companies or networks, cloud
          computing services or data centers, or other Third-Party Services), or
          third party failures (including, without limitation, failures of third
          parties on which our Services may rely, such as bank failures that
          prevent depositor access to funds in those banks);
        </li>
        <li>
          The value of Digital Currency or Digital Collectibles (including,
          without limitation, NFTs) may be highly volatile (including, without
          limitation, rapidly decreasing or falling to zero), and any Digital
          Currency or Digital Collectible may lose some or all of its value due
          to, among other things, lack of demand, market manipulation, changes
          to applicable law, government regulation;
        </li>
        <li>
          Information or instructions transmitted over the Internet may be
          delayed, interrupted, stolen, or altered due to software or hardware
          malfunction or third-party attacks;
        </li>
        <li>
          Your Digital Currency or Digital Collectibles (including, without
          limitation, NFTs) may be subject to scams or other types of fraud
          perpetrated by third parties;
        </li>
        <li>
          The risks described in Section 11 with respect to Peer-to-Peer
          Transactions may occur;
        </li>
        <li>
          You assume full responsibility and agree that Tokenwave will not have
          any responsibility or liability for any losses, damages, or other harm
          you may incur in connection with any or all such risks; and
        </li>
        <li>
          • Your Droppp Wallet is not a depository account and are not protected
          by any government-backed depositor compensation, insurance or
          guarantee scheme;
        </li>
      </ul>
      <p>
        <b>
          You hereby irrevocably waive, release, and discharge any and all
          claims, whether known or unknown to you, against Tokenwave or any of
          its affiliates and their respective shareholders, members, directors,
          officers, employees, attorneys, agents, representatives, service
          providers, suppliers, and other business partners related to any of
          the risks set forth herein, any Peer-to-Peer Transactions, disputes
          between users (including, without limitation, any disputes related to
          Peer-to-Peer Transactions), or the acts or omissions of third parties
          (including, without limitation, the acts of any other users in
          connection with Peer-to-Peer Transactions). You waive application of
          Section 1542 of the Civil Code of the State of California, or any
          similar statute or law of any other jurisdiction. Section 1542 reads
          as follows: “A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS THAT THE
          CREDITOR OR RELEASING PARTY DOES NOT KNOW OR SUSPECT TO EXIST IN HIS,
          HER, OR THEIR FAVOR AT THE TIME OF EXECUTING THE RELEASE AND THAT, IF
          KNOWN BY HIM, HER, OR THEM, WOULD HAVE MATERIALLY AFFECTED HIS, HER,
          OR THEIR SETTLEMENT WITH THE DEBTOR OR RELEASED PARTY.”
        </b>
      </p>

      <h3>14. Transfer and Processing Data</h3>
      <p>
        In order for us to provide our Services, you agree that we may process,
        transfer and store information about you in the United States and other
        countries, where you may not have the same rights and protections as you
        do under local law.
      </p>
      <h3>15. Dispute Resolution; Binding Arbitration</h3>
      <p>
        <b>
          PLEASE READ THIS SECTION CAREFULLY BECAUSE IT REQUIRES YOU AND
          TOKENWAVE TO ARBITRATE CERTAIN DISPUTES AND CLAIMS AND LIMITS THE
          MANNER IN WHICH WE CAN SEEK RELIEF FROM EACH OTHER. ARBITRATION
          PRECLUDES YOU AND TOKENWAVE FROM SUING IN COURT OR HAVING A JURY
          TRIAL. YOU AND TOKENWAVE AGREE THAT ARBITRATION WILL BE SOLELY ON AN
          INDIVIDUAL BASIS AND NOT AS A CLASS ARBITRATION, CLASS ACTION, OR ANY
          OTHER KIND OF REPRESENTATIVE PROCEEDING. TOKENWAVE AND YOU ARE EACH
          WAIVING THE RIGHT TO TRIAL BY A JURY. NO CLASS OR REPRESENTATIVE
          ACTIONS OR ARBITRATIONS ARE ALLOWED UNDER THIS ARBITRATION AGREEMENT.
        </b>
      </p>
      <ul className="!list-[lower-alpha]">
        <li>
          <b>Informal Dispute Resolution Prior to Arbitration.</b> For any
          dispute or claim that you have against Tokenwave, that Tokenwave has
          against you or that you have, or Tokenwave has, in each case relating
          in any way to these Terms or the Sites, or any aspect of the
          relationship between you and Tokenwave as relates to these Terms or
          our Services (collectively, “Claims” and each a “Claim”), you and
          Tokenwave agree to attempt to first resolve the Claim informally via
          the following process. If you assert a Claim against Tokenwave, you
          will first contact Tokenwave by sending a written notice of your Claim
          (“Claimant Notice”) to Tokenwave by certified mail addressed to
          Tokenwave, LLC, Attn: Legal, 2802 Wetmore Avenue Everett, WA 98201 or
          by email to support@droppp.io. The Claimant Notice must (i) include
          your name, residence address, email address, and telephone number;
          (ii) describe the nature and basis of the Claim; and (iii) set forth
          the specific relief sought. If Tokenwave asserts a Claim against you,
          Tokenwave will first contact you by sending a written notice of
          Tokenwave’s Claim (“Tokenwave Notice”) to you via email to the primary
          email address associated with your account. The Tokenwave Notice must
          (x) include the name of a Tokenwave contact and the contact’s email
          address and telephone number; (y) describe the nature and basis of the
          Claim; and (z) set forth the specific relief sought. If you and
          Tokenwave cannot reach an agreement to resolve the Claim within thirty
          (30) days after you or Tokenwave receives such a Notice, then either
          party may submit the Claim to binding arbitration as set forth below.
          The statute of limitations and any filing fee deadlines shall be
          tolled for thirty (30) days from the date that either you or Tokenwave
          first send a Claimant Notice or a Tokenwave Notice, so that the
          parties can engage in this informal dispute-resolution process.
        </li>
        <li>
          <b>Claims Subject to Binding Arbitration; Exceptions.</b> Except for
          (i) small claims disputes in which you or Tokenwave seek to bring an
          individual action in small claims court located in the jurisdiction of
          your billing address or (ii) any disputes exclusively related to the
          intellectual property or intellectual property rights of you or
          Tokenwave, including any disputes in which you or Tokenwave seek
          injunctive or other equitable relief for the alleged unlawful use of
          your or Tokenwave’s intellectual property or other infringement of
          your or Tokenwave’s intellectual property rights (“IP Claims”), all
          Claims, whether based in contract, tort, statute, fraud,
          misrepresentation, or any other legal theory, including Claims that
          are not related to intellectual property or intellectual property
          rights but are jointly filed with IP Claims, that are not resolved in
          accordance with Section 15(a) will be resolved by a neutral arbitrator
          through final and binding arbitration instead of in a court by a judge
          or jury. Such Claims include, without limitation, disputes arising out
          of or relating to interpretation or application of this arbitration
          provision, including the enforceability, revocability, or validity of
          the arbitration provision or any portion of the arbitration provision.
          The arbitrator will have the authority to grant any remedy or relief
          that would otherwise be available in court.
        </li>
        <li>
          <b>Federal Arbitration Act.</b> These Terms affect interstate
          commerce, and the enforceability of this Section will be substantively
          and procedurally governed by the Federal Arbitration Act, 9 U.S.C. §
          1, et seq. (the “FAA”), to the extent permitted by law. As limited by
          the FAA, these Terms, and the AAA Rules, the arbitrator will have
          exclusive authority to make all procedural and substantive decisions
          regarding any dispute and to grant any remedy that would otherwise be
          available in court, including the power to determine the question of
          arbitrability.
        </li>
        <li>
          <b>Arbitration Procedures.</b> All Claims must be submitted to the
          American Arbitration Association (the “AAA”) and will be resolved
          through binding arbitration before one arbitrator. The AAA administers
          arbitration pursuant to the due process standards set forth by the AAA
          and rules set forth by the AAA, and the then-current version of the
          AAA’s Consumer Arbitration Rules, which are available on the AAA’s
          website (adr.org), as amended by these Terms as follows, will apply to
          any arbitration between you and Tokenwave:
          <ul className="list-disc">
            <li>
              YOU AND TOKENWAVE AGREE THAT ANY ARBITRATION UNDER THESE TERMS
              WILL TAKE PLACE ON AN INDIVIDUAL BASIS; CLASS ARBITRATIONS AND
              CLASS ACTIONS ARE NOT PERMITTED, AND YOU AND TOKENWAVE ARE
              AGREEING TO GIVE UP THE ABILITY TO PARTICIPATE IN A CLASS ACTION.
              The arbitrator may conduct only an individual arbitration and,
              except as described below for the additional procedures to govern
              if twenty-five (25) or more similar or coordinated claims are
              asserted against Tokenwave or you by the same or coordinated
              counsel, may not consolidate more than one individual’s claims,
              preside over any type of class or representative proceeding, or
              preside over any proceeding involving more than one individual.
            </li>
            <li>
              For any arbitration you initiate, you will pay the consumer filing
              fee, and Tokenwave will pay the remaining AAA fees and costs. For
              any arbitration initiated by Tokenwave, Tokenwave will pay all AAA
              fees and costs.
            </li>
            <li>
              For all arbitrations where the Claims asserted are $25,000 or
              less, the arbitration shall be resolved according to the AAA’s
              Procedures for the Resolution of Disputes through Document
              Submission, and for all other arbitrations the following procedure
              will apply: (i) the arbitrator will conduct hearings, if any, by
              teleconference or videoconference, rather than by personal
              appearances, unless the arbitrator determines upon request by you
              or by us that an in-person hearing is appropriate; (ii) any
              in-person appearances will be held at a location that is
              reasonably convenient to both parties with due consideration of
              their ability to travel and other pertinent circumstances, and
              (iii) if the parties are unable to agree on a location, such
              determination will be made by the AAA or by the arbitrator.
            </li>
            <li>
              If you or Tokenwave submits a dispute to arbitration and the
              arbitrator orders any exchange of information, you and Tokenwave
              agree to cooperate to seek from the arbitrator protection for any
              confidential, proprietary, trade secret, or otherwise sensitive
              information, documents, testimony, and/or other materials that
              might be exchanged or the subject of discovery in the arbitration.
              You and Tokenwave agree to seek such protection before any such
              information, documents, testimony, and/or materials are exchanged
              or otherwise become the subject of discovery in the arbitration.
            </li>
            <li>
              In addition, the provisions of Federal Rule of Civil Procedure 68
              shall apply and be enforced by the arbitrator.
            </li>
            <li>
              The arbitrator’s decision will follow these Terms and will be
              final and binding. The arbitrator will have authority to award
              temporary, interim or permanent injunctive relief or relief
              providing for specific performance of these Terms, but only to the
              extent necessary to provide relief warranted by the individual
              claim before the arbitrator. The award rendered by the arbitrator
              may be confirmed and enforced in any court having jurisdiction
              thereof. Notwithstanding any of the foregoing, nothing in these
              Terms will preclude you from bringing issues to the attention of
              federal, state or local agencies and, if the law allows, they can
              seek relief against us for you.
            </li>
            <li>
              The AAA Supplementary Rules for Multiple Case Filings and the AAA
              Multiple Consumer Case Filing Fee Schedule will apply if
              twenty-five (25) or more similar claims are asserted against
              Tokenwave or against you by the same or coordinated counsel or are
              otherwise coordinated. In addition to the application of the AAA
              Supplementary Rules for Multiple Case Filings and the AAA Multiple
              Consumer Case Filing Fee Schedule, you and Tokenwave understand
              and agree that, when twenty-five (25) or more similar claims are
              asserted against Tokenwave or you by the same or coordinated
              counsel or are otherwise coordinated, resolution of your or
              Tokenwave’s Claim might be delayed. For such coordinated actions,
              you and Tokenwave also agree to the following coordinated
              bellwether process:
              <ol>
                <li>
                  Counsel for the claimants and counsel for Tokenwave shall each
                  select ten (10) cases (per side) to proceed first in
                  individual arbitration proceedings. The remaining cases shall
                  be deemed filed for purposes of the statute of limitations but
                  not for the purpose of assessing AAA fees. No AAA fees shall
                  be assessed in connection with those cases until they are
                  selected to proceed to individual arbitration proceedings as
                  part of a bellwether process. If the parties are unable to
                  resolve the remaining cases after the conclusion of the
                  initial twenty (20) proceedings, each side shall select
                  another ten (10) cases (per side) to proceed to individual
                  arbitration proceedings.
                </li>
                <li>
                  A single arbitrator shall preside over each case. Only one
                  case may be assigned to each arbitrator as part of a
                  bellwether process unless the parties agree otherwise.
                </li>
                <li>
                  This bellwether process shall continue, consistent with the
                  parameters identified above, until all the claims included in
                  these coordinated filings, including your case, are
                  adjudicated or otherwise resolved.
                </li>
                <li>
                  The statute of limitations and any filing fee deadlines shall
                  be tolled for claims subject to this bellwether process from
                  the time the first cases are selected for a bellwether process
                  until the time your or Tokenwave’s case is selected for a
                  bellwether process, withdrawn, or otherwise resolved.
                </li>
                <li>
                  A court shall have authority to enforce this paragraph and, if
                  necessary, to enjoin the mass filing or prosecution of
                  arbitration demands against Tokenwave or you.
                </li>
              </ol>
            </li>
          </ul>
        </li>
        <li>
          <b>
            One Year to Assert Claims. To the extent permitted by law, any Claim
            or dispute by you or Tokenwave arising out of or related to these
            Terms or the Sites, or any aspect of the relationship between you
            and Tokenwave as relates to your use of the Sites, in each case
            other than IP Claims, must be filed within one year after such Claim
            or dispute arose; otherwise, the claim is permanently barred, which
            means that you and Tokenwave will not have the right to assert the
            Claim.
          </b>
        </li>
        <li>
          <b>Opting Out of Arbitration.</b> You have the right to opt out of
          binding arbitration within 30 days of the date you first accepted
          these Terms by providing us notice of your opt-out via email at
          support@droppp.io or by certified mail addressed to Tokenwave, LLC,
          Attn: Legal, 2802 Wetmore Avenue Everett, WA 98201. In order to be
          effective, the opt-out notice must include your full name, and mailing
          address, and email address and clearly indicate your intent to opt out
          of binding arbitration. By opting out of binding arbitration, you are
          agreeing to resolve disputes in accordance with Section 17.
        </li>
        <li>
          <b>Rejection of Future Arbitration Changes.</b> You may reject any
          change we make to Section 15 (except address changes) by personally
          signing and sending us notice within 30 days of the change via email
          at support@droppp.io or by certified mail addressed to Tokenwave, LLC,
          Attn: Legal, 2802 Wetmore Avenue Everett, WA 98201. If you do, the
          most recent version of Section 15 before the change you rejected will
          apply.
        </li>
        <li>
          <b>Severability.</b> If any portion of this Section 15 is found to be
          unenforceable or unlawful for any reason, including but not limited to
          because it is found to be unconscionable, (i) the unenforceable or
          unlawful provision will be severed from these Terms; (ii) severance of
          the unenforceable or unlawful provision will have no impact whatsoever
          on the remainder of this Section 15 or the parties’ ability to compel
          arbitration of any remaining claims on an individual basis pursuant to
          this Section 15; and (iii) to the extent that any claims must
          therefore proceed on a class, collective, consolidated, or
          representative basis, such claims must be litigated in a civil court
          of competent jurisdiction and not in arbitration. The litigation of
          those claims will be stayed pending the outcome of any individual
          claims in arbitration. Further, if any part of this Section 15 is
          found to prohibit an individual claim seeking public injunctive
          relief, that provision will have no effect to the extent such relief
          is allowed to be sought out of arbitration, and the remainder of this
          Section 15 will be enforceable.
        </li>
      </ul>

      <h3>16. Third-Party Services</h3>
      <p>
        In making our Services available, Tokenwave (a) uses third-party hosting
        infrastructures and other technology or third-party services and (b) may
        provide information about or links to third-party products or services
        ((a) and (b), “Third-Party Services” and the vendors that provide the
        Third-Party Services, the “Third-Party Vendors”). Without limiting the
        foregoing, (i) the Platform and NFTs reside on the Worldwide Asset
        eXchange, or WAX (“WAX”) blockchain (the “WAX Blockchain”), the WAX
        Blockchain is a Third-Party Service and WAX is a Third-Party Vendor,
        (ii) the Droppp Wallet and key management infrastructure from Turnkey is
        a Third-Party Service and Turnkey is a Third-Party Vendor, (iii) the
        Payment Services, to the extent provided by our Payment Processors, is a
        Third-Party Service, and the services provided by the Verification
        Services Provider is a Third-Party Service and (iv) our Payment
        Processors and the Verification Services Provider are Third-Party
        Vendors. Tokenwave does not endorse or make any representations or
        warranties regarding any Third-Party Services or any Third-Party
        Vendors, including, without limitation, as to their effectiveness,
        reliability, accuracy, safety, or appropriateness, or your results. Your
        use of any Third-Party Services and interactions or communications with
        Third-Party Vendors is solely between you and the applicable Third-Party
        Vendor (including, without limitation, being subject to any terms of
        service or terms of use governing those Third-Party Services). Tokenwave
        is not responsible or liable in any manner for any Third-Party Services
        or your interactions or communications with Third-Party Vendors, or for
        any loss, damage or harm of any sort incurred as the result of any of
        the foregoing. You access and use such Third-Party Services, and
        interact with and communicate with such Third-Party Vendors, at your own
        risk.
      </p>

      <h3>17. Governing Law and Venue</h3>
      <p>
        Any dispute arising from these Terms and your use of our Services will
        be governed by and construed and enforced in accordance with the laws of
        the State of New York, except to the extent preempted by U.S. federal
        law, without regard to conflict of law rules or principles (whether of
        the State of New York or any other jurisdiction) that would cause the
        application of the laws of any other jurisdiction. Any dispute between
        the parties that is not subject to arbitration or cannot be heard in
        small claims court will be resolved in the state or federal courts of
        the State of New York and the United States, respectively, sitting in
        the Southern District of New York.
      </p>
      <h3>18. Modifying and Terminating our Services</h3>
      <p>
        We reserve the right to modify our Services or to suspend or stop
        providing all or portions of our Services at any time. You also have the
        right to stop using our Services at any time. We are not responsible for
        any loss or harm related to your inability to access or use our
        Services.
      </p>
      <h3>19. Severability</h3>
      <p>
        If any portion of these Terms (other than Section 15) is found to be
        unenforceable or unlawful for any reason, including but not limited to
        because it is found to be unconscionable, (a) the unenforceable or
        unlawful provision will be severed from these Terms; (b) severance of
        the unenforceable or unlawful provision will have no impact whatsoever
        on the remainder of these Terms; and (c) the unenforceable or unlawful
        provision may be revised to the extent required to render the Terms
        enforceable or valid, and the rights and responsibilities of the parties
        will be interpreted and enforced accordingly, so as to preserve these
        Terms and the intent of these Terms to the fullest possible extent.
      </p>
      <h3>20. Miscellaneous</h3>
      <p>
        These Terms, including any terms referenced in these Terms, constitute
        the entire agreement between you and Tokenwave relating to your access
        to and use of our Services. Notwithstanding any other provisions of
        these Terms, all sections of these Terms that by their nature extend
        beyond the discontinuation of our Services or other termination of your
        access to our Services, including, without limitation, Sections 1, 3, 4,
        5, 6, 7 (except for the license granted therein), and 8 through 20, will
        survive termination or discontinuation. The failure of Tokenwave to
        exercise or enforce any right or provision of these Terms will not
        operate as a waiver of such right or provision. The section titles in
        these Terms are for convenience only and have no legal or contractual
        effect. Except as otherwise provided herein, these Terms are intended
        solely for the benefit of the parties and are not intended to confer
        third-party beneficiary rights upon any other person or entity. You
        agree that communications and transactions between us may be conducted
        electronically. These Terms may not be transferred, assigned or
        delegated by you, by operation of law or otherwise, without our prior
        written consent, and any attempted transfer, assignment or delegation
        without such consent will be void and without effect. We may freely
        transfer, assign or delegate these Terms or our Services, in whole or in
        part, without your prior written consent.
      </p>
    </div>
  )
}
